import { useCallback } from 'react';
import { useUnityBuildStore } from '../../stores/store';
import { useStore } from 'zustand';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { v4 as uuidv4 } from 'uuid';
import { getAdjustedCursorPosition } from '../../components/svgElements/takeoffHelper';
import { useHandleGlobalStateState } from '../../hooks/useHandleGlobalState';
import { useKeyBoardEventStore } from '../../stores/keyEventStore';
import { useSelectionStore } from '../../stores/SelectionStore';
import { safeAssemblySwitch, isActivelyWorkingOnAssembly } from '../../utils/assemblyProtection';
import { useDragManager } from '../../components/svgElements/DragManager';
import { calculateCursorPosition, calculateCenterOfPoints } from './mouseEventUtils';
import { Position2D, SelectedPoint } from '../types/eventTypes';
import { TakeoffAssembly } from '../../types/AssemblyItemType';
import { Point } from '../../types/Point';
import { useAnnotationStore } from '../../components/annotations/annotationStore';

/**
 * Properties for useLeftButtonEvents hook
 */
export interface LeftButtonEventProps {
  /** Reference to SVG element */
  svgRef: React.RefObject<SVGSVGElement>;

  /** Reference to current cursor position */
  cursorPositionRef: React.MutableRefObject<Position2D>;

  /** Function to update cursor position in store */
  setCursorPosition: (position: Position2D) => void;

  /** Currently selected points */
  selectedPoints: SelectedPoint[];

  /** Function to update selected points locally */
  setSelectedPointsLocal: (points: SelectedPoint[]) => void;

  /** Function to set drag selection state */
  setIsDragSelecting: (isDragging: boolean) => void;

  /** Function to set the drag start position */
  setDragStartPosition: (position: Position2D) => void;

  /** List of all assemblies that can be interacted with */
  assembliesList: TakeoffAssembly[];

  /** Whether paste preview is currently showing */
  showPastePreview?: boolean;

  /** Reference to points being previewed for paste */
  pastePreviewPoints?: React.MutableRefObject<Point[]>;

  /** Reference to position where paste operation should occur */
  pastePositionRef?: React.MutableRefObject<Position2D | null>;

  /** Whether annotation mode is currently active */
  isAnnotating?: boolean;

  /** Function to add a new note - may return null if operation fails */
  addNote?: (x: number, y: number) => string | null;

  /** Function to set the annotation mode state */
  setIsAnnotating?: (isAnnotating: boolean) => void;
}

/**
 * Custom hook for handling left mouse button events in the SVG editor
 */
export const useLeftButtonEvents = ({
  svgRef,
  cursorPositionRef,
  setCursorPosition,
  selectedPoints,
  setSelectedPointsLocal,
  setIsDragSelecting,
  setDragStartPosition,
  assembliesList,
  showPastePreview,
  pastePreviewPoints,
  pastePositionRef,
  isAnnotating: propIsAnnotating,
  addNote: propAddNote,
  setIsAnnotating: propSetIsAnnotating,
}: LeftButtonEventProps) => {
  const {
    angleOptions,
    isAnnotatingTakeoff,
    isDragging,
    isEditingPoint,
    isEditingStyle,
    isEditingPointFillColor,
    isEditingPointBorderColor,
    isEditingLineColor,
    isInsertingPoint,
    setMeasuredPoints,
    hasPointEditorInteraction,
    setHasPointEditorInteraction,
  } = useStore(useUnityBuildStore);

  const { currentTakeoffAssembly, setCurrentTakeoffAssembly } = useStore(useAssemblyStore);
  const { setSelectedPoints } = useStore(useSelectionStore);
  const { isDragging: isDraggingPoints } = useDragManager();
  const { selectedPoints: globalSelectedPoints } = useStore(useSelectionStore);
  const storeAnnotationValues = useStore(useAnnotationStore);

  // Use the props directly instead of from the store if provided
  const finalIsAnnotating =
    propIsAnnotating !== undefined ? propIsAnnotating : storeAnnotationValues.isAnnotating;

  const handleGlobalStates = useHandleGlobalStateState();

  /**
   * Handles point selection when clicking on an existing point
   */
  const handlePointSelection = useCallback(
    (pointId: string) => {
      // Check if we have an existing group selection (annotations + takeoff points)
      const annotationStore = useAnnotationStore.getState();
      const hasExistingGroup =
        annotationStore?.selectedShapeIds?.length > 0 || selectedPoints.length > 0;

      // Check if we've clicked on a point that's already selected
      const isSelectedPoint = selectedPoints.some(sp => sp.pointId === pointId);

      if (hasExistingGroup) {
        // If we have an existing group, maintain it and just add/remove this point
        if (isSelectedPoint) {
          // Remove this point from selection
          const updatedPoints = selectedPoints.filter(sp => sp.pointId !== pointId);
          setSelectedPointsLocal(updatedPoints);
          setSelectedPoints(updatedPoints);
        } else {
          // Add this point to selection
          const assembly = assembliesList.find(assembly => {
            return assembly.pointsList?.some(
              (point: { pointid: string }) => point.pointid === pointId
            );
          });

          if (assembly) {
            const point = assembly.pointsList?.find(
              (p: { pointid: string }) => p.pointid === pointId
            );

            if (point) {
              const newSelectedPoint: SelectedPoint = {
                pointId: pointId,
                assemblyId: assembly.assemblyid,
                originalX: point.x,
                originalY: point.y,
              };

              const updatedPoints = [...selectedPoints, newSelectedPoint];
              setSelectedPointsLocal(updatedPoints);
              setSelectedPoints(updatedPoints);

              // Switch to this assembly if needed
              if (assembly.assemblyid !== currentTakeoffAssembly?.assemblyid) {
                setCurrentTakeoffAssembly(assembly);
              }
            }
          }
        }
        return true;
      } else if (isSelectedPoint && selectedPoints.length > 0) {
        // Original logic for when no existing group
        const assembly = assembliesList.find(assembly => {
          return assembly.pointsList?.some(
            (point: { pointid: string }) => point.pointid === pointId
          );
        });

        if (assembly) {
          // Check if we're actively working on a different assembly
          if (
            assembly.assemblyid !== currentTakeoffAssembly?.assemblyid &&
            isActivelyWorkingOnAssembly()
          ) {
            // Silently block interaction with points from different assemblies
            return false;
          }

          // Use safe assembly switch to protect active work
          const switchAllowed = safeAssemblySwitch(
            assembly.assemblyid,
            currentTakeoffAssembly?.assemblyid || null,
            () => setCurrentTakeoffAssembly(assembly)
          );
          return switchAllowed;
        }
      }

      return false;
    },
    [
      assembliesList,
      selectedPoints,
      currentTakeoffAssembly,
      setCurrentTakeoffAssembly,
      setSelectedPointsLocal,
      setSelectedPoints,
    ]
  );

  /**
   * Handles adding a new point to the current assembly
   */
  const handleAddPoint = useCallback(() => {
    if (!currentTakeoffAssembly || isInsertingPoint || !isAnnotatingTakeoff) {
      return false;
    }

    const pointsArray = Array.isArray(currentTakeoffAssembly.pointsList)
      ? currentTakeoffAssembly.pointsList
      : [];

    const kbStore = useKeyBoardEventStore.getState();
    let adjustedCursorPosition = cursorPositionRef.current;

    // Adjust cursor position based on snap mode and angle options
    if (
      pointsArray.length > 0 &&
      (currentTakeoffAssembly.assemblymeasurementtype === 'length' ||
        currentTakeoffAssembly.assemblymeasurementtype === 'area')
    ) {
      const lastPoint = pointsArray[pointsArray.length - 1];
      adjustedCursorPosition = getAdjustedCursorPosition(
        lastPoint,
        cursorPositionRef.current,
        angleOptions,
        kbStore.snapMode,
        currentTakeoffAssembly
      );
    }

    // Create updated assembly with new point
    const updatedItem = {
      ...currentTakeoffAssembly,
      pointsList: [
        ...pointsArray,
        {
          pointid: uuidv4(),
          x: adjustedCursorPosition.x,
          y: adjustedCursorPosition.y,
          angle: 0,
          verticallength: 0,
          allowtrailingline: true,
          allowtrailingmeasurement: true,
          isincludedincount: kbStore.continousMode,
          isselected: true,
          isactive: true,
        },
      ],
    };

    setCurrentTakeoffAssembly(updatedItem);
    return true;
  }, [
    currentTakeoffAssembly,
    isInsertingPoint,
    isAnnotatingTakeoff,
    cursorPositionRef,
    angleOptions,
    setCurrentTakeoffAssembly,
  ]);

  /**
   * Handles adding a measurement point
   */
  const handleAddMeasurementPoint = useCallback(() => {
    const kbStore = useKeyBoardEventStore.getState();
    if (kbStore.isMeasuring) {
      setMeasuredPoints(prev => [...prev, cursorPositionRef.current]);
      return true;
    }
    return false;
  }, [cursorPositionRef, setMeasuredPoints]);

  /**
   * Handles starting drag selection
   */
  const handleStartDragSelection = useCallback(
    (isHoveringElement: boolean) => {
      const kbStore = useKeyBoardEventStore.getState();
      const annotationStore = useAnnotationStore.getState();
      const unityBuildStore = useUnityBuildStore.getState();
      const selectionStore = useSelectionStore.getState();

      // Set conditionals to prevent dragging from executing
      if (
        kbStore.isMeasuring || //<--- TODO: Make this state part of the annotation store for consistancy of seperating annotations with takeoffAnnotations
        currentTakeoffAssembly ||
        annotationStore.activeAnnotationType !== null || //<--- we set this to null so that we can use annotation tools without starting a drag selection
        annotationStore.selectedShapeId !== null || //<--- NEW: if a shape is selected, block drag-to-select so shape handles can be used
        unityBuildStore.isDragging || //<--- Prevent drag-to-select when a point is already being dragged
        selectionStore.groupDragState.isDraggingGroup || //<--- Prevent drag-to-select when group dragging
        isDraggingPoints || //<--- Prevent drag-to-select when DragManager is actively dragging points
        (globalSelectedPoints.length > 0 && isHoveringElement) //<--- Prevent drag-to-select when hovering over selected points that could be dragged
      ) {
        return false;
      }

      setIsDragSelecting(true);
      setDragStartPosition(cursorPositionRef.current);

      // Only clear selection if user is starting a new drag and not hovering over element
      if (!isHoveringElement) {
        setSelectedPointsLocal([]);
        setSelectedPoints([]);
      }

      return true;
    },
    [
      cursorPositionRef,
      setIsDragSelecting,
      setDragStartPosition,
      setSelectedPointsLocal,
      setSelectedPoints,
      currentTakeoffAssembly,
      isDraggingPoints,
      globalSelectedPoints,
    ]
  );

  /**
   * Main handler for left button (button0) mouse events
   */
  const handleTakeoffButton0MouseEvents = useCallback(
    async (event: MouseEvent | React.MouseEvent) => {
      event.stopPropagation();
      if (!svgRef.current || event.button !== 0) return;

      // Ignore Alt (reserved for other tools); treat Ctrl/Shift as normal clicks
      if (event.altKey) return;

      // Check if the user is currently focused on a text input element
      const eventTarget = event.target as HTMLElement;
      const activeElement = document.activeElement;

      // Prevent our handler if user is editing text or focused on text inputs
      if (
        activeElement &&
        (activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'INPUT' ||
          activeElement.getAttribute('contenteditable') === 'true' ||
          eventTarget.tagName === 'TEXTAREA' ||
          eventTarget.tagName === 'INPUT' ||
          eventTarget.getAttribute('contenteditable') === 'true')
      ) {
        // Don't interfere with text editing
        return;
      }

      // Calculate cursor position
      const position = calculateCursorPosition(event, svgRef);
      if (position) {
        cursorPositionRef.current = position;
        setCursorPosition(position);
      }

      // FEATURE: Check if we have an active paste preview first
      // If so, left-click should freeze the position
      if (
        showPastePreview &&
        pastePreviewPoints &&
        pastePreviewPoints.current &&
        pastePreviewPoints.current.length > 0 &&
        pastePositionRef
        // setPastePreviewOffset
      ) {
        // Set the paste position to the current cursor
        pastePositionRef.current = { ...cursorPositionRef.current };

        // Set a flag that the paste position is frozen (to show in point editor)
        (window as any).__pastePositionFrozen = true;

        // Store the frozen position globally so PointEditor can access it
        (window as any).__frozenPastePosition = { ...cursorPositionRef.current };

        // Calculate the paste preview offset so the paste action can use it
        if (pastePreviewPoints && pastePreviewPoints.current.length > 0) {
          const center = calculateCenterOfPoints(pastePreviewPoints.current);
          const offset = {
            x: cursorPositionRef.current.x - center.x,
            y: cursorPositionRef.current.y - center.y,
          };
          (window as any).__currentPastePreviewOffset = offset;
        }

        // Make sure we have a currentTakeoffAssembly set - this could be null if the user switched documents
        const assemblyStore = useAssemblyStore.getState();
        if (!assemblyStore.currentTakeoffAssembly) {
          const selectionStore = useSelectionStore.getState();
          // Use type assertion to access the copiedObjects property
          const selectionStoreAny = selectionStore as any;
          const copiedObjects = selectionStoreAny.copiedObjects || [];

          if (copiedObjects.length > 0 && copiedObjects[0].assemblyProperties) {
            // Create a temporary currentTakeoffAssembly just for the paste operation
            assemblyStore.setCurrentTakeoffAssembly({
              ...copiedObjects[0].assemblyProperties,
              // Use new objectId and breakoutId since we switched documents
              objectid: useUnityBuildStore.getState().selectedObject?.id || '',
              breakoutid: useUnityBuildStore.getState().selectedBreakout?.id || '',
            });
          }
        }

        // Open point editor with paste options
        try {
          useAssemblyStore.getState().setIsViewingTakeoffEditor(true);
        } catch {}
        useUnityBuildStore.getState().setIsEditingPoint({
          isEditingPoint: true,
          isViewingTakeoffEditor: true,
          x: event.clientX,
          y: event.clientY,
        });

        // Stop further processing of the click
        return;
      }

      // Modified behavior: if PointEditor is open but no interaction yet,
      // close it and start annotating on current assembly instead of resetting
      if (isEditingPoint) {
        if (!hasPointEditorInteraction) {
          // Close editor, keep current assembly, and enter annotate mode
          useUnityBuildStore.getState().setIsEditingPoint({
            isEditingPoint: false,
            isViewingTakeoffEditor: false,
          });
          setHasPointEditorInteraction(false);

          // Ensure we have a currentTakeoffAssembly and enter annotate mode
          const assemblyStore = useAssemblyStore.getState();
          if (assemblyStore.currentTakeoffAssembly) {
            useUnityBuildStore.getState().setIsAnnotatingTakeoff(true);
            // Set flag to indicate an active run has started via screen click
            useUnityBuildStore.getState().setHasActiveRun(true);
          }
        } else {
          // Prior behavior: reset state when editor was interacted with
          handleGlobalStates({
            resetOption: 'resetTakeoff',
          });
          return;
        }
      }

      // Handle reset state with global handler
      if (isDragging) {
        handleGlobalStates({
          resetOption: 'resetTakeoff',
        });
        return;
      }

      // Skip all actions when style editing is active
      if (
        isEditingStyle ||
        isEditingPointFillColor ||
        isEditingPointBorderColor ||
        isEditingLineColor
      ) {
        return;
      }

      // Check if we're hovering over an SVG element
      const target = event.target as SVGElement;
      const isHoveringElement = target.tagName !== 'svg' && target.tagName !== 'rect';
      const pointId = target.getAttribute('data-pointid');

      // Handle different click scenarios in order of priority
      if (!finalIsAnnotating) {
        // NEW: If a shape is being dragged or a shape is selected, skip starting drag-select
        const annotationStoreLive = useAnnotationStore.getState();
        if (annotationStoreLive.isDraggingShape || annotationStoreLive.selectedShapeId) {
          return;
        }

        if (isHoveringElement && pointId && handlePointSelection(pointId)) {
          return;
        }

        if (handleAddPoint()) {
          return;
        }

        if (handleAddMeasurementPoint()) {
          return;
        }

        // If none of the above, try to start drag selection
        handleStartDragSelection(isHoveringElement);
      }
    },
    [
      svgRef,
      cursorPositionRef,
      setCursorPosition,
      isDragging,
      isEditingPoint,
      isEditingStyle,
      isEditingPointFillColor,
      isEditingPointBorderColor,
      isEditingLineColor,
      handleGlobalStates,
      handlePointSelection,
      handleAddPoint,
      handleAddMeasurementPoint,
      handleStartDragSelection,
      showPastePreview,
      pastePreviewPoints,
      pastePositionRef,
      finalIsAnnotating,
      hasPointEditorInteraction,
      setHasPointEditorInteraction,
    ]
  );

  return { handleTakeoffButton0MouseEvents };
};
